import React, { useEffect, useState, useMemo, useRef } from "react";
import { motion } from "framer-motion";
import { X, AlertTriangle, MessageSquare, Users, GitBranch, ShoppingBag, StickyNote, Clock, MapPin, HelpCircle, Megaphone, Search, ChevronDown, ChevronRight, Eye, Activity, Zap, CheckCircle2 } from "lucide-react";
import api, { errText } from "@/lib/api";
import { toast } from "sonner";
import { Avatar, EstadoPunto, BarraAvance, Hero, Etiqueta, Dato, fmtInt, fmtUSD, fmtPct, fmtBrecha, colorBrecha, haceCuanto } from "@/components/panel/PanelUI";

function horaCorta(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleDateString("es-AR", { day: "2-digit", month: "short" }) + " · " + d.toLocaleTimeString("es-AR", { hour: "2-digit", minute: "2-digit" });
  } catch { return ""; }
}

const puntoColor = { gold: "#C9A84C", coral: "#EC835A", normal: "#3A3A3A" };
const estadoProc = { andando: { c: "#0CA30C", t: "Andando" }, definido: { c: "#EC835A", t: "Definido" }, borrador: { c: "#6B6B6B", t: "En borrador" } };

export default function FichaCuenta({ cuenta, onClose, onCambio }) {
  const [d, setD] = useState(null);
  const [eventos, setEventos] = useState(null);
  const [resumen, setResumen] = useState(null);
  const [convo, setConvo] = useState(null);
  const [verConvo, setVerConvo] = useState(false);
  const [notas, setNotas] = useState("");
  const [fechaIni, setFechaIni] = useState("");
  const [editFecha, setEditFecha] = useState(false);
  const [regen, setRegen] = useState(null); // {proc, preview, loading, aplicando}
  const [convoStale, setConvoStale] = useState(false); // hay mensajes nuevos y el panel de chats está abierto
  const [apagando, setApagando] = useState(false);
  const firmaRef = useRef(null);

  const apagarConector = async () => {
    if (!window.confirm("¿Apagar el conector con ChatGPT? Se revocan todos los accesos de esta cuenta.")) return;
    setApagando(true);
    try {
      await api.post(`/admin/empresa/${cuenta.id}/conector/apagar`);
      toast.success("Conector apagado. ChatGPT ya no puede leer ni reportar.");
      const det = await api.get(`/admin/empresa/${cuenta.id}/detalle`);
      setD(det.data);
    } catch (e) { toast.error(errText(e)); }
    setApagando(false);
  };

  const recargarFicha = async () => {
    try {
      const [det, tl, rs] = await Promise.all([
        api.get(`/admin/empresa/${cuenta.id}/detalle`),
        api.get(`/admin/empresa/${cuenta.id}/timeline`),
        api.get(`/admin/empresa/${cuenta.id}/resumen`),
      ]);
      setD(det.data); setEventos(tl.data.eventos || []); setResumen(rs.data);
    } catch { /* silencioso: es un refresco en vivo */ }
  };

  const abrirRegen = async (p) => {
    setRegen({ proc: p, preview: null, loading: true, aplicando: false });
    try {
      const r = await api.post(`/admin/implementacion/${p.id}/regenerar-preview`);
      setRegen((s) => (s && s.proc?.id === p.id ? { ...s, preview: r.data, loading: false } : s));
    } catch (e) { toast.error(errText(e)); setRegen(null); }
  };
  const aplicarRegen = async () => {
    if (!regen?.proc) return;
    setRegen((s) => ({ ...s, aplicando: true }));
    try {
      await api.post(`/admin/implementacion/${regen.proc.id}/regenerar`);
      toast.success("Pedido regenerado con la plantilla actual. Los pasos y el avance del cliente quedaron intactos.");
      setRegen(null);
      const det = await api.get(`/admin/empresa/${cuenta.id}/detalle`);
      setD(det.data);
    } catch (e) { toast.error(errText(e)); setRegen((s) => (s ? { ...s, aplicando: false } : s)); }
  };

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const [det, tl, rs] = await Promise.all([
          api.get(`/admin/empresa/${cuenta.id}/detalle`),
          api.get(`/admin/empresa/${cuenta.id}/timeline`),
          api.get(`/admin/empresa/${cuenta.id}/resumen`),
        ]);
        if (!alive) return;
        setD(det.data); setNotas(det.data.empresa.notas || "");
        setFechaIni((det.data.empresa.fecha_inicio || "").slice(0, 10));
        setEventos(tl.data.eventos || []);
        setResumen(rs.data);
      } catch { if (alive) { setEventos([]); setResumen(false); } }
    })();
    return () => { alive = false; };
  }, [cuenta.id]);

  // Sondeo en vivo: cada 8s mira el "pulso" (firma liviana). Si cambió, refresca resumen/timeline/detalle.
  // Guardas: solo con la pestaña visible y se corta solo a los 15 minutos para no golpear la base sin fin.
  useEffect(() => {
    let alive = true;
    const inicio = Date.now();
    firmaRef.current = null;
    const tick = async () => {
      if (!alive) return;
      if (document.visibilityState !== "visible") return;
      if (Date.now() - inicio > 15 * 60 * 1000) { clearInterval(id); return; }
      try {
        const r = await api.get(`/admin/empresa/${cuenta.id}/pulso`);
        const f = r.data.firma;
        if (firmaRef.current !== null && f !== firmaRef.current) {
          await recargarFicha();
          setConvoStale(true); // si el panel de chats está abierto, avisamos que hay nuevos
        }
        firmaRef.current = f;
      } catch { /* silencioso */ }
    };
    const id = setInterval(tick, 8000);
    return () => { alive = false; clearInterval(id); };
  }, [cuenta.id]);

  const recargarConvo = async () => {
    try { const r = await api.get(`/admin/empresa/${cuenta.id}/conversaciones`); setConvo(r.data.mensajes || []); }
    catch { setConvo([]); }
    setConvoStale(false);
  };
  const abrirConvo = async () => {
    setVerConvo((v) => !v);
    if (convo === null) { await recargarConvo(); }
  };
  const marcarMala = async (id) => {
    try {
      const r = await api.post(`/admin/mensaje/${id}/marcar-mala`);
      setConvo((c) => c.map((m) => m.id === id ? { ...m, marcada_mala: r.data.marcada_mala } : m));
      toast.success(r.data.marcada_mala ? "Guardada como mala para revisar el prompt." : "Se sacó de la lista.");
    } catch { toast.error("No pude guardarla."); }
  };
  const guardarNotas = async () => {
    try { await api.post(`/admin/empresa/${cuenta.id}/notas`, { notas }); toast.success("Nota guardada."); }
    catch { toast.error("No pude guardar la nota."); }
  };
  const guardarFecha = async () => {
    try {
      await api.patch(`/admin/empresa/${cuenta.id}/fecha-inicio`, { fecha_inicio: fechaIni });
      // Releer el detalle completo (dia_txt, día, brecha se recalculan en el server) y repintar.
      const det = await api.get(`/admin/empresa/${cuenta.id}/detalle`);
      setD(det.data);
      setFechaIni((det.data.empresa.fecha_inicio || "").slice(0, 10));
      setEditFecha(false);
      toast.success("Fecha de inicio actualizada. El día del ciclo se recalcula solo.");
      onCambio && onCambio(); // refresca la lista del panel (estado, trabadas, HOY) y la ficha abierta
    } catch (e) { toast.error(errText(e)); }
  };

  const emp = d?.empresa || {};
  const pr = d?.promesa || {};
  const moneda = emp.moneda || "USD";
  const S = ({ icon: Icon, children }) => (
    <div className="font-sans flex items-center gap-2 mb-4 mt-9" style={{ fontSize: 13, color: "#8A8A8A", fontWeight: 500 }}>
      {Icon && <Icon size={15} strokeWidth={1.7} />}{children}
    </div>
  );

  return (
    <div className="fixed inset-0 z-[100] flex justify-end" data-testid="ficha-cuenta">
      <div className="absolute inset-0" style={{ background: "rgba(0,0,0,0.6)" }} onClick={onClose} data-testid="ficha-backdrop" />
      <motion.div initial={{ x: 40, opacity: 0 }} animate={{ x: 0, opacity: 1 }} transition={{ duration: 0.25 }}
        className="relative h-full overflow-y-auto" style={{ width: "min(620px, 100vw)", background: "#0A0A0A", borderLeft: "1px solid #1F1F1F" }}>
        <div className="p-8">
          {/* ÚLTIMO VISTO POR EL EQUIPO — para no duplicar asistencia (confidencialidad) */}
          {d?.ultimo_visto && (
            <div className="flex items-center gap-2 mb-5 px-3 py-2 rounded-lg" data-testid="ficha-ultimo-visto"
              style={{ background: "#0F0F0F", border: "1px solid #1F1F1F" }}>
              <Eye size={13} strokeWidth={1.8} style={{ color: "#8A8A8A" }} />
              <span className="font-sans" style={{ fontSize: 12, color: "#9A9A9A" }}>
                Último visto por <span style={{ color: "#C9A84C" }}>{d.ultimo_visto.nombre || d.ultimo_visto.email}</span>
                {d.ultimo_visto.rol ? ` · ${d.ultimo_visto.rol}` : ""} · {haceCuanto(d.ultimo_visto.creado_en)}
              </span>
            </div>
          )}
          {/* CABECERA */}
          <div className="flex items-start justify-between mb-6">
            <div className="flex items-center gap-4">
              <Avatar nombre={cuenta.nombre} size={48} />
              <div>
                <div className="font-sans" style={{ fontSize: 20, color: "#F5F0E8", fontWeight: 500 }}>{cuenta.nombre}</div>
                <div className="font-sans mt-0.5" style={{ fontSize: 12, color: "#8A8A8A" }}>
                  {[emp.rubro, emp.tamano].filter(Boolean).join(" · ") || "—"}
                </div>
                <div className="mt-1.5"><EstadoPunto estado={cuenta.estado_panel} /></div>
              </div>
            </div>
            <button onClick={onClose} className="text-gris hover:text-crema" data-testid="ficha-cerrar"><X size={20} /></button>
          </div>
          <div className="grid grid-cols-3 gap-3 mb-2">
            <MiniDato label="Vendió" valor={emp.closer || "—"} texto />
            <div className="rounded-xl p-3" style={{ background: "#0F0F0F", border: "1px solid #1F1F1F" }} data-testid="ficha-fecha-inicio">
              {!editFecha ? (
                <button onClick={() => setEditFecha(true)} className="text-left w-full" data-testid="ficha-fecha-editar">
                  <div className="font-sans truncate" style={{ fontSize: 14, color: "#E8E8E8" }}>{emp.fecha_inicio ? new Date(emp.fecha_inicio).toLocaleDateString("es-AR") : "—"}</div>
                  <div className="mt-1"><Etiqueta>Alta · tocá para editar</Etiqueta></div>
                </button>
              ) : (
                <div>
                  <input type="date" value={fechaIni} onChange={(e) => setFechaIni(e.target.value)} data-testid="ficha-fecha-input"
                    className="font-sans w-full rounded" style={{ background: "#141414", border: "1px solid #2A2A2A", color: "#E8E8E8", fontSize: 13, padding: "4px 6px" }} />
                  <div className="flex gap-3 mt-1.5">
                    <button onClick={guardarFecha} data-testid="ficha-fecha-guardar" style={{ fontSize: 11, color: "#C9A84C" }}>guardar</button>
                    <button onClick={() => setEditFecha(false)} data-testid="ficha-fecha-cancelar" style={{ fontSize: 11, color: "#7A7A7A" }}>cancelar</button>
                  </div>
                </div>
              )}
            </div>
            <MiniDato label="Día" valor={emp.dia_txt || `${emp.dia ?? 0}/30`} texto />
          </div>

          {/* RESUMEN DE INTERVENCIÓN — para entender en 30 segundos, antes de leer los chats */}
          <ResumenIntervencion resumen={resumen} />

          {/* LA PROMESA */}
          <S icon={Clock}>La promesa</S>
          <div className="rounded-xl p-5" style={{ background: "#0F0F0F", border: "1px solid #1F1F1F" }}>
            <div className="flex items-end justify-between mb-3">
              <div><Hero size={38}>{fmtPct(pr.avance)}</Hero><span className="font-sans ml-2" style={{ fontSize: 13, color: "#8A8A8A" }}>avanzado</span></div>
              <div className="text-right"><div className="font-serif" style={{ fontSize: 30, color: colorBrecha(pr.brecha), fontVariantNumeric: "tabular-nums" }}>{fmtBrecha(pr.brecha)}</div><Etiqueta>brecha</Etiqueta></div>
            </div>
            <BarraAvance avance={pr.avance} tiempo={pr.tiempo} height={10} />
            <div className="grid grid-cols-2 gap-4 mt-5">
              <div><Hero size={30}>{fmtInt(pr.horas_vivas)}<span style={{ fontSize: 16, color: "#8A8A8A" }}> / {fmtInt(pr.horas_detectadas)} h</span></Hero><div className="mt-1"><Etiqueta>horas vivas / detectadas</Etiqueta></div></div>
              <div className="text-right"><Hero size={30}>{emp.valor_hora ? `${moneda} ${fmtInt(pr.plata_viva)}` : "—"}</Hero><div className="mt-1"><Etiqueta>ahorro por mes</Etiqueta></div></div>
            </div>
          </div>

          {/* LAS PERSONAS */}
          <S icon={Users}>Las personas</S>
          <Card2>
            {(d?.personas || []).length === 0 ? <Vacio t="Todavía no entró nadie." /> : d.personas.map((p) => (
              <div key={p.id} className="flex items-center px-4" style={{ minHeight: 52, borderTop: "1px solid #1A1A1A" }} data-testid={`persona-${p.id}`}>
                <Avatar nombre={p.nombre} size={30} />
                <div className="ml-3 flex-1 min-w-0">
                  <div className="font-sans truncate" style={{ fontSize: 14, color: "#E8E8E8" }}>{p.nombre}{p.rol ? <span style={{ color: "#7A7A7A" }}> · {p.rol}</span> : null}</div>
                  <div className="font-sans" style={{ fontSize: 12, color: p.entro ? "#8A8A8A" : "#D03B3B" }}>{p.entro ? `entró ${haceCuanto(p.ultima_actividad)}` : "nunca entró"}</div>
                </div>
                <Dato className="text-sm">{fmtPct(p.avance)}</Dato>
              </div>
            ))}
          </Card2>

          {/* LÍNEA DE TIEMPO */}
          <S icon={Clock}>Su historia</S>
          {eventos === null ? <Cargando /> : eventos.length === 0 ? <Vacio t="Todavía no hizo nada." /> : (
            <div className="relative pl-6" style={{ borderLeft: "1px solid #1F1F1F" }} data-testid="ficha-timeline">
              {eventos.map((ev) => (
                <div key={ev.id} className="relative pb-5">
                  <span className="absolute rounded-full" style={{ left: -25, top: 5, width: 9, height: 9, background: puntoColor[ev.color] || "#3A3A3A", boxShadow: ev.color !== "normal" ? `0 0 8px ${(puntoColor[ev.color] || "#3A3A3A")}66` : "none" }} />
                  <div className="flex items-start gap-2">
                    {ev.raro && <AlertTriangle size={13} strokeWidth={1.6} style={{ color: "#EC835A", marginTop: 3 }} />}
                    <div>
                      <div className="font-sans" style={{ fontSize: 14, color: ev.raro ? "#EC835A" : "#D8D8D8" }}>{ev.texto}{ev.quien ? <span style={{ color: "#6B6B6B" }}> · {ev.quien}</span> : null}</div>
                      <div className="font-sans mt-0.5" style={{ fontSize: 11, color: "#666" }}>{horaCorta(ev.creado_en)}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* SUS PROCESOS */}
          <S icon={GitBranch}>Sus procesos</S>
          <Card2>
            {(d?.procesos || []).length === 0 ? <Vacio t="Todavía no definió ninguno." /> : d.procesos.map((p) => {
              const e = estadoProc[p.estado] || { c: "#6B6B6B", t: p.estado };
              const rz = p.forma_razonamiento;
              return (
                <div key={p.id} style={{ borderTop: "1px solid #1A1A1A" }}>
                <div className="flex items-center px-4" style={{ minHeight: 50 }} data-testid={`proceso-${p.id}`}>
                  <span className="rounded-full mr-3" style={{ width: 8, height: 8, background: e.c }} />
                  <div className="flex-1 min-w-0 font-sans truncate" style={{ fontSize: 14, color: "#E8E8E8" }}>{p.nombre}</div>
                  <span className="font-sans mr-4" style={{ fontSize: 12, color: "#8A8A8A" }}>{e.t}{p.estado === "andando" && (p.probado === "verificado" ? " · probado" : " · sin probar")}</span>
                  {p.vida_estado && (
                    <span className="font-sans mr-4" style={{ fontSize: 11, color: p.vida_estado === "abandonado" ? "#D03B3B" : p.vida_estado === "a_veces" ? "#EC835A" : "#0CA30C" }} data-testid={`proceso-vida-${p.id}`}>
                      {p.vida_estado === "abandonado" ? (p.vida_motivo ? `LO DEJÓ · ${p.vida_motivo}` : "LO DEJÓ") : p.vida_estado === "a_veces" ? "LO USA A VECES" : "VIVO"}
                    </span>
                  )}
                  {p.plantilla_vieja && (
                    <span data-testid={`proceso-plantilla-vieja-${p.id}`} className="font-sans mr-3" style={{ fontSize: 10, color: "#EC835A", border: "1px solid rgba(236,131,90,0.5)", borderRadius: 6, padding: "1px 6px", whiteSpace: "nowrap" }}>PLANTILLA VIEJA</span>
                  )}
                  {p.complejo && (
                    <span data-testid={`proceso-complejo-${p.id}`} className="font-sans mr-3" style={{ fontSize: 10, color: "#C9A84C", border: "1px solid rgba(201,168,76,0.5)", borderRadius: 6, padding: "1px 6px", whiteSpace: "nowrap" }}>COMPLEJO</span>
                  )}
                  <Dato className="text-sm">{fmtInt(p.horas)}<span style={{ color: "#6B6B6B" }}> h</span></Dato>
                  {p.tiene_impl && (
                    <button onClick={() => abrirRegen(p)} data-testid={`regenerar-${p.id}`} className="font-sans ml-3 rounded-lg" style={{ fontSize: 11, color: p.plantilla_vieja ? "#C9A84C" : "#8A8A8A", border: `1px solid ${p.plantilla_vieja ? "rgba(201,168,76,0.5)" : "#2A2A2A"}`, background: "#161616", padding: "3px 9px", whiteSpace: "nowrap" }}>Regenerar</button>
                  )}
                </div>
                {rz && (rz.descartadas?.length || rz.restricciones || rz.sin_solucion) && (
                  <div className="px-4 pb-3 font-sans" style={{ fontSize: 11, color: "#8A8A8A" }} data-testid={`proceso-razonamiento-${p.id}`}>
                    <div style={{ color: "#C9A84C", marginBottom: 2 }}>Por qué se eligió {p.forma ? `«${p.forma}»` : "esta forma"}</div>
                    {rz.motivo && <div>· {rz.motivo}</div>}
                    {Array.isArray(rz.descartadas) && rz.descartadas.length > 0 && (
                      <div style={{ marginTop: 2 }}>Descartadas: {rz.descartadas.map((x) => `${x.forma} (${x.motivo})`).join(" · ")}</div>
                    )}
                    {rz.sin_solucion && <div style={{ color: "#EC835A", marginTop: 2 }}>Se queda corto: {rz.sin_solucion}</div>}
                  </div>
                )}
                </div>
              );
            })}
          </Card2>

          {/* CONECTOR CON CHATGPT */}
          <S icon={Zap}>Conector con ChatGPT</S>
          <Card2>
            {(() => {
              const c = d?.conector || {};
              return (
                <div className="px-4 py-3" data-testid="conector-chatgpt">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="rounded-full" style={{ width: 8, height: 8, background: c.activo ? "#0CA30C" : "#6B6B6B" }} />
                      <span className="font-sans" style={{ fontSize: 14, color: "#E8E8E8" }} data-testid="conector-estado">{c.activo ? "Conectado" : "Sin conectar"}</span>
                    </div>
                    {c.activo && (
                      <button onClick={apagarConector} disabled={apagando} data-testid="conector-apagar" className="font-sans rounded-lg"
                        style={{ fontSize: 11, color: "#EC835A", border: "1px solid rgba(236,131,90,0.5)", background: "#161616", padding: "3px 10px" }}>
                        {apagando ? "Apagando…" : "APAGAR CONECTOR"}
                      </button>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-3 mt-3">
                    <MiniDato label="Último uso" valor={c.ultimo_uso ? horaCorta(c.ultimo_uso) : "—"} texto />
                    <MiniDato label="Corridas (30d)" valor={c.corridas_30d ?? 0} />
                    <MiniDato label="Salieron bien (30d)" valor={c.ok_30d ?? 0} />
                  </div>
                  {c.conectado_en && <div className="font-sans mt-2" style={{ fontSize: 11, color: "#6B6B6B" }}>Conectado {horaCorta(c.conectado_en)}</div>}
                </div>
              );
            })()}
          </Card2>

          {/* MODAL REGENERAR PEDIDO · ver antes de aplicar */}
          {regen && (
            <div data-testid="regen-modal" style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.72)", zIndex: 80, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }} onClick={() => !regen.aplicando && setRegen(null)}>
              <div onClick={(e) => e.stopPropagation()} style={{ background: "#0E0E0E", border: "1px solid #2A2A2A", borderRadius: 14, width: "min(1000px, 96vw)", maxHeight: "90vh", display: "flex", flexDirection: "column" }}>
                <div className="flex items-center px-5 py-4" style={{ borderBottom: "1px solid #1A1A1A" }}>
                  <div className="flex-1 font-sans truncate" style={{ color: "#E8E8E8", fontSize: 15 }}>Regenerar pedido · <span style={{ color: "#8A8A8A" }}>{regen.proc?.nombre}</span></div>
                  <button onClick={() => !regen.aplicando && setRegen(null)} data-testid="regen-cerrar" style={{ color: "#8A8A8A" }}><X size={18} /></button>
                </div>
                <div className="px-5 py-3 font-sans" style={{ fontSize: 12.5, color: "#C9A84C", borderBottom: "1px solid #1A1A1A" }}>
                  Vas a reemplazar el TEXTO del pedido por la versión actual de la plantilla. Los pasos que escribió la persona y su avance (pasos tildados, en qué paso va) NO se tocan.
                </div>
                <div style={{ overflow: "auto", padding: 16 }} data-testid="regen-comparacion">
                  {regen.loading ? (
                    <div className="font-sans" style={{ color: "#8A8A8A", padding: "24px 0", textAlign: "center" }}>Generando la versión nueva para comparar…</div>
                  ) : regen.preview ? (
                    (regen.preview.new.pedidos || []).map((np, i) => {
                      const op = (regen.preview.old.pedidos || [])[i];
                      return (
                        <div key={i} style={{ marginBottom: 18 }}>
                          {(regen.preview.new.mixto || regen.preview.old.mixto) && (
                            <div className="font-sans" style={{ color: "#C9A84C", fontSize: 12, marginBottom: 6 }}>Pedido {i + 1}{np.ia ? ` · ${np.ia}` : ""}</div>
                          )}
                          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                            <div>
                              <div className="mono-label" style={{ color: "#8A8A8A", fontSize: 10, marginBottom: 4 }}>ANTES (v{regen.preview.old.plantilla_version})</div>
                              <pre data-testid={`regen-old-${i}`} className="font-sans" style={{ whiteSpace: "pre-wrap", fontSize: 12, color: "#9A9A9A", background: "#0A0A0A", border: "1px solid #1A1A1A", borderRadius: 8, padding: 10, maxHeight: 340, overflow: "auto" }}>{op ? op.pedido : "—"}</pre>
                            </div>
                            <div>
                              <div className="mono-label" style={{ color: "#0CA30C", fontSize: 10, marginBottom: 4 }}>AHORA (v{regen.preview.new.plantilla_version})</div>
                              <pre data-testid={`regen-new-${i}`} className="font-sans" style={{ whiteSpace: "pre-wrap", fontSize: 12, color: "#DADADA", background: "#0A0A0A", border: "1px solid #22331f", borderRadius: 8, padding: 10, maxHeight: 340, overflow: "auto" }}>{np.pedido}</pre>
                            </div>
                          </div>
                        </div>
                      );
                    })
                  ) : (
                    <div className="font-sans" style={{ color: "#D03B3B" }}>No pude generar la comparación.</div>
                  )}
                </div>
                <div className="flex items-center justify-end gap-3 px-5 py-4" style={{ borderTop: "1px solid #1A1A1A" }}>
                  <button onClick={() => !regen.aplicando && setRegen(null)} data-testid="regen-cancelar" className="font-sans rounded-lg" style={{ fontSize: 13, color: "#B8B8B8", border: "1px solid #2A2A2A", background: "#161616", padding: "8px 16px" }}>Cancelar</button>
                  <button onClick={aplicarRegen} disabled={regen.loading || regen.aplicando || !regen.preview} data-testid="regen-confirmar" className="font-sans rounded-lg" style={{ fontSize: 13, color: "#0E0E0E", background: (regen.loading || regen.aplicando || !regen.preview) ? "#5a5030" : "#C9A84C", padding: "8px 16px", fontWeight: 600 }}>{regen.aplicando ? "Aplicando…" : "Aplicar la versión nueva"}</button>
                </div>
              </div>
            </div>
          )}

          {/* CONVERSACIONES */}
          <S icon={MessageSquare}>Sus conversaciones</S>
          <button onClick={abrirConvo} data-testid="ficha-ver-conversaciones" className="font-sans px-4 py-2 rounded-lg" style={{ background: "#161616", border: "1px solid #2A2A2A", color: "#D8D8D8", fontSize: 13 }}>
            {verConvo ? "Ocultar" : "Leer los chats completos"}
          </button>
          {verConvo && (
            <div className="mt-4" data-testid="ficha-conversaciones">
              {convoStale && (
                <button onClick={recargarConvo} data-testid="ficha-convo-actualizar" className="font-sans mb-3 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg"
                  style={{ background: "#1A1508", border: "1px solid #3A3010", color: "#D8BE6A", fontSize: 12 }}>
                  <Zap size={12} /> Hay actividad nueva · actualizar chats
                </button>
              )}
              {convo === null ? <Cargando /> : convo.length === 0 ? <Vacio t="Sin conversaciones." /> : (
                <ChatsAgrupados mensajes={convo} onMarcarMala={marcarMala} />
              )}
            </div>
          )}

          {/* FUERA DE ALCANCE */}
          <S icon={ShoppingBag}>Lo que pidió y no cubrimos</S>
          <Card2>
            {(d?.fuera_alcance || []).length === 0 ? <Vacio t="Nada fuera de alcance por ahora." /> : d.fuera_alcance.map((f, i) => (
              <div key={i} className="px-4 py-3" style={{ borderTop: "1px solid #1A1A1A" }} data-testid={`fuera-${i}`}>
                <div className="font-sans" style={{ fontSize: 14, color: "#E8E8E8" }}>{f.titulo}</div>
                {f.motivo && <div className="font-sans mt-0.5" style={{ fontSize: 12, color: "#7A7A7A" }}>{f.motivo}</div>}
              </div>
            ))}
          </Card2>

          {/* NOTAS */}
          <S icon={StickyNote}>Notas</S>
          <textarea value={notas} onChange={(e) => setNotas(e.target.value)} onBlur={guardarNotas} data-testid="ficha-notas"
            className="font-sans w-full rounded-xl px-4 py-3" placeholder="Lo que pasó en la última llamada…"
            style={{ background: "#0F0F0F", border: "1px solid #262626", color: "#E8E8E8", fontSize: 14, minHeight: 90, resize: "vertical" }} />
        </div>
      </motion.div>
    </div>
  );
}

function Card2({ children }) { return <div className="rounded-xl overflow-hidden" style={{ background: "#0F0F0F", border: "1px solid #1F1F1F" }}>{children}</div>; }
function Vacio({ t }) { return <div className="font-sans px-4 py-4" style={{ fontSize: 13, color: "#6B6B6B" }}>{t}</div>; }
function Cargando() { return <div className="font-sans px-1 py-2" style={{ fontSize: 13, color: "#6B6B6B" }}>Cargando…</div>; }
function MiniDato({ label, valor, texto = false }) {
  return (
    <div className="rounded-xl p-3" style={{ background: "#0F0F0F", border: "1px solid #1F1F1F" }}>
      {texto ? <div className="font-sans truncate" style={{ fontSize: 14, color: "#E8E8E8" }}>{valor}</div> : <Dato className="text-lg">{valor}</Dato>}
      <div className="mt-1"><Etiqueta>{label}</Etiqueta></div>
    </div>
  );
}

function haceCuantoDias(n) {
  if (n === null || n === undefined) return "";
  if (n <= 0) return "hoy";
  if (n === 1) return "hace 1 día";
  return `hace ${n} días`;
}

// Bloque que responde de un vistazo: dónde está, qué pidió y no obtuvo, dónde se trabó y qué decirle.
function ResumenIntervencion({ resumen }) {
  if (resumen === null) {
    return (
      <div className="rounded-2xl p-5 mt-2" style={{ background: "#0F0F0F", border: "1px solid #1F1F1F" }} data-testid="resumen-intervencion-cargando">
        <div className="font-sans" style={{ fontSize: 13, color: "#6B6B6B" }}>Armando el resumen…</div>
      </div>
    );
  }
  if (resumen === false) return null;

  const { donde = {}, preguntas_pendientes = [], trabas = [], senales = [], acciones = [], necesita_atencion, procesos = [], actividad_semana = [], ultimo } = resumen;
  const trabasGraves = trabas.filter((t) => t.veces >= 2);
  const bordeColor = necesita_atencion ? "#5A2E2E" : "#1F1F1F";

  const Bloque = ({ icon: Icon, titulo, children, color = "#8A8A8A" }) => (
    <div className="rounded-xl p-4" style={{ background: "#0B0B0B", border: "1px solid #1A1A1A" }}>
      <div className="flex items-center gap-2 mb-2" style={{ color }}>
        <Icon size={14} strokeWidth={1.8} />
        <span className="font-sans" style={{ fontSize: 11, letterSpacing: "0.06em", textTransform: "uppercase", fontWeight: 600 }}>{titulo}</span>
      </div>
      {children}
    </div>
  );

  return (
    <div className="rounded-2xl p-5 mt-2" data-testid="resumen-intervencion"
      style={{ background: necesita_atencion ? "#140D0D" : "#0D0D0D", border: `1px solid ${bordeColor}` }}>
      <div className="flex items-center justify-between mb-4">
        <div className="font-sans" style={{ fontSize: 13, color: "#C9A84C", fontWeight: 600, letterSpacing: "0.04em" }}>
          RESUMEN · para intervenir sin leer todo
        </div>
        {necesita_atencion && (
          <span className="font-sans inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full" data-testid="resumen-badge-atencion"
            style={{ background: "#3A1414", border: "1px solid #6B2020", color: "#F0A0A0", fontSize: 11, fontWeight: 600 }}>
            <AlertTriangle size={12} /> Necesita atención
          </span>
        )}
      </div>

      {/* SEÑALES destacadas */}
      {senales.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-4" data-testid="resumen-senales">
          {senales.map((s, i) => (
            <span key={i} data-testid={`resumen-senal-${s.tipo}`} className="font-sans inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg"
              style={{ background: s.grave ? "#2A1010" : "#1A1508", border: `1px solid ${s.grave ? "#5A2020" : "#3A3010"}`, color: s.grave ? "#EC8A8A" : "#D8BE6A", fontSize: 12 }}>
              <AlertTriangle size={12} /> {s.texto}
            </span>
          ))}
        </div>
      )}

      <div className="grid md:grid-cols-2 gap-3">
        {/* DÓNDE ESTÁ */}
        <Bloque icon={MapPin} titulo="Dónde está" color="#C9A84C">
          <div className="font-sans" style={{ fontSize: 14, color: "#E8E8E8" }} data-testid="resumen-donde-etapa">{donde.etapa || "Todavía no arrancó el diagnóstico"}</div>
          {donde.paso_actual && <div className="font-sans mt-0.5" style={{ fontSize: 13, color: "#A0A0A0" }} data-testid="resumen-donde-paso">{donde.paso_actual}</div>}
          <div className="font-sans mt-1.5" style={{ fontSize: 12, color: donde.dias_sin_avanzar >= 3 ? "#EC835A" : "#7A7A7A" }} data-testid="resumen-sin-avanzar">
            {donde.dias_sin_avanzar === null || donde.dias_sin_avanzar === undefined ? "sin actividad registrada" : `no avanza ${haceCuantoDias(donde.dias_sin_avanzar)}`}
          </div>
          <div className="font-sans mt-2 pt-2" style={{ borderTop: "1px solid #1A1A1A", fontSize: 12, color: "#9A9A9A" }} data-testid="resumen-ultimo">
            {ultimo ? <>Lo último: <span style={{ color: "#CFCFCF" }}>{ultimo.texto}</span> · {haceCuanto(ultimo.creado_en)}</> : "Todavía no hizo nada en la plataforma"}
          </div>
        </Bloque>

        {/* QUÉ HAY QUE DECIRLE */}
        <Bloque icon={Megaphone} titulo="Qué hay que decirle" color="#EC835A">
          <ul className="space-y-1.5" data-testid="resumen-acciones">
            {acciones.map((a, i) => (
              <li key={i} className="font-sans flex gap-2" style={{ fontSize: 13, color: "#E0E0E0" }}>
                <span style={{ color: "#EC835A" }}>›</span><span>{a}</span>
              </li>
            ))}
          </ul>
        </Bloque>

        {/* QUÉ PIDIÓ Y NO OBTUVO */}
        <Bloque icon={HelpCircle} titulo="Qué pidió y no obtuvo">
          {preguntas_pendientes.length === 0 ? (
            <div className="font-sans" style={{ fontSize: 13, color: "#6B6B6B" }}>Nada pendiente.</div>
          ) : (
            <ul className="space-y-2" data-testid="resumen-preguntas">
              {preguntas_pendientes.map((p, i) => (
                <li key={i} data-testid={`resumen-pregunta-${i}`}>
                  <div className="font-sans" style={{ fontSize: 13, color: "#D8D8D8" }}>«{p.texto}»</div>
                  <div className="font-sans" style={{ fontSize: 11, color: "#7A7A7A" }}>{p.quien ? `${p.quien} · ` : ""}{p.motivo}</div>
                </li>
              ))}
            </ul>
          )}
        </Bloque>

        {/* DÓNDE SE TRABÓ */}
        <Bloque icon={AlertTriangle} titulo="Dónde se trabó">
          {trabas.length === 0 ? (
            <div className="font-sans" style={{ fontSize: 13, color: "#6B6B6B" }}>No tocó NO ME SALE.</div>
          ) : (
            <ul className="space-y-1.5" data-testid="resumen-trabas">
              {trabas.map((t, i) => (
                <li key={i} data-testid={`resumen-traba-${i}`} className="font-sans flex items-center justify-between gap-2" style={{ fontSize: 13, color: t.veces >= 2 ? "#EC835A" : "#D8D8D8" }}>
                  <span>Paso {t.paso ?? "?"} de «{t.proceso}»</span>
                  <span className="font-mono" style={{ fontSize: 12, color: t.veces >= 2 ? "#EC835A" : "#8A8A8A" }}>{t.veces}×</span>
                </li>
              ))}
            </ul>
          )}
          {trabasGraves.length > 0 && <div className="font-sans mt-2" style={{ fontSize: 11, color: "#EC835A" }}>Se trabó 2+ veces en el mismo paso.</div>}
        </Bloque>
      </div>

      {/* SUS PROCESOS HOY: andando vs trabados */}
      <div className="mt-3">
        <Bloque icon={GitBranch} titulo="Sus procesos hoy">
          {procesos.length === 0 ? (
            <div className="font-sans" style={{ fontSize: 13, color: "#6B6B6B" }}>Todavía no definió ningún proceso.</div>
          ) : (
            <div className="flex flex-wrap gap-2" data-testid="resumen-procesos">
              {procesos.map((p) => {
                const c = p.trabado ? { bg: "#2A1010", bd: "#5A2020", fg: "#EC8A8A" }
                  : p.estado === "andando" ? { bg: "#0C1F0C", bd: "#1E3A1E", fg: "#7BD97B" }
                  : p.estado === "definido" ? { bg: "#1A1508", bd: "#3A3010", fg: "#D8BE6A" }
                  : { bg: "#141414", bd: "#2A2A2A", fg: "#9A9A9A" };
                const et = p.trabado ? "trabado" : p.estado === "andando" ? "andando" : p.estado === "definido" ? "definido" : "en borrador";
                return (
                  <span key={p.id} data-testid={`resumen-proceso-${p.id}`} className="font-sans inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg"
                    style={{ background: c.bg, border: `1px solid ${c.bd}`, color: c.fg, fontSize: 12 }}>
                    {p.trabado ? <AlertTriangle size={11} /> : <CheckCircle2 size={11} />}
                    {p.nombre} · {et}{p.no_me_sale > 0 ? ` · ${p.no_me_sale}× NO ME SALE` : ""}
                  </span>
                );
              })}
            </div>
          )}
        </Bloque>
      </div>

      {/* ACTIVIDAD DE LA ÚLTIMA SEMANA (por día; es actividad, no minutos) */}
      <div className="mt-3">
        <Bloque icon={Activity} titulo="Actividad de la última semana">
          {actividad_semana.length === 0 ? (
            <div className="font-sans" style={{ fontSize: 13, color: "#6B6B6B" }}>No tuvo actividad en los últimos 7 días.</div>
          ) : (
            <div className="space-y-2" data-testid="resumen-actividad">
              {actividad_semana.map((a) => (
                <div key={a.fecha} className="flex items-start gap-3" data-testid={`resumen-actividad-${a.fecha}`}>
                  <div className="font-mono" style={{ fontSize: 11, color: "#8A8A8A", minWidth: 62, paddingTop: 1 }}>{fechaCorta(a.fecha)}</div>
                  <div className="flex flex-wrap gap-1.5 flex-1">
                    {a.detalle.map((x, i) => (
                      <span key={i} className="font-sans" style={{ fontSize: 11, color: "#CFCFCF", background: "#141414", border: "1px solid #262626", borderRadius: 6, padding: "1px 7px" }}>
                        {x.que}{x.veces > 1 ? ` ×${x.veces}` : ""}
                      </span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </Bloque>
      </div>
    </div>
  );
}

function fechaCorta(iso) {
  try {
    const d = new Date(iso + "T00:00:00");
    return d.toLocaleDateString("es-AR", { day: "2-digit", month: "short" });
  } catch { return iso; }
}

function tituloGrupo(m) {
  const base = m.contexto_label || "General";
  if ((m.contexto === "proceso" || m.contexto === "implementacion") && m.proceso_nombre) {
    return `${base} · ${m.proceso_nombre}`;
  }
  return base;
}

// Chats agrupados por conversación (diagnóstico / cada proceso / implementación / mesa de trabajo).
// Arrancan cerrados; el buscador filtra sobre TODOS los mensajes de la cuenta, no solo el grupo abierto.
function ChatsAgrupados({ mensajes, onMarcarMala }) {
  const [q, setQ] = useState("");
  const [abierto, setAbierto] = useState({});
  const [filtro, setFiltro] = useState("todos");

  const grupos = useMemo(() => {
    const map = new Map();
    for (const m of mensajes) {
      const key = `${m.contexto || "general"}|${m.proceso_id || ""}`;
      if (!map.has(key)) map.set(key, { key, titulo: tituloGrupo(m), mensajes: [] });
      map.get(key).mensajes.push(m);
    }
    return [...map.values()].map((g) => ({
      ...g, count: g.mensajes.length, ultimo: g.mensajes[g.mensajes.length - 1]?.creado_en,
    }));
  }, [mensajes]);

  const ql = q.trim().toLowerCase();
  const buscando = ql.length > 0;
  const coincide = (m) => !buscando || (m.texto || "").toLowerCase().includes(ql) || (m.quien || "").toLowerCase().includes(ql);
  const toggle = (key) => setAbierto((s) => ({ ...s, [key]: !s[key] }));

  const gruposVisibles = grupos
    .filter((g) => (filtro === "todos" ? true : g.key === filtro))
    .map((g) => ({ ...g, matches: buscando ? g.mensajes.filter(coincide) : g.mensajes }))
    .filter((g) => (buscando ? g.matches.length > 0 : true));
  const totalMatches = buscando ? gruposVisibles.reduce((n, g) => n + g.matches.length, 0) : 0;

  return (
    <div data-testid="chats-agrupados">
      <div className="flex flex-col sm:flex-row gap-2 mb-3">
        <div className="flex items-center gap-2 flex-1 px-3 rounded-lg" style={{ background: "#0F0F0F", border: "1px solid #262626" }}>
          <Search size={14} style={{ color: "#7A7A7A" }} />
          <input value={q} onChange={(e) => setQ(e.target.value)} data-testid="chats-buscador"
            placeholder="Buscar en todos los chats de la cuenta…"
            className="font-sans w-full bg-transparent py-2" style={{ fontSize: 13, color: "#E8E8E8", outline: "none" }} />
        </div>
        <select value={filtro} onChange={(e) => setFiltro(e.target.value)} data-testid="chats-filtro"
          className="font-sans rounded-lg px-3 py-2" style={{ background: "#0F0F0F", border: "1px solid #262626", color: "#D8D8D8", fontSize: 13 }}>
          <option value="todos">Todas las conversaciones</option>
          {grupos.map((g) => <option key={g.key} value={g.key}>{g.titulo} ({g.count})</option>)}
        </select>
      </div>
      {buscando && (
        <div className="font-sans mb-2" style={{ fontSize: 12, color: "#8A8A8A" }} data-testid="chats-buscador-info">
          {totalMatches === 0 ? "Sin coincidencias." : `${totalMatches} mensaje(s) coinciden`}
        </div>
      )}
      <div className="space-y-2">
        {gruposVisibles.map((g) => {
          const open = buscando || abierto[g.key];
          return (
            <div key={g.key} className="rounded-xl overflow-hidden" style={{ background: "#0B0B0B", border: "1px solid #1A1A1A" }} data-testid={`chat-grupo-${g.key}`}>
              <button onClick={() => !buscando && toggle(g.key)} data-testid={`chat-grupo-toggle-${g.key}`}
                className="w-full flex items-center gap-2 px-4 py-3 text-left" style={{ cursor: buscando ? "default" : "pointer" }}>
                {!buscando && (open ? <ChevronDown size={15} style={{ color: "#8A8A8A" }} /> : <ChevronRight size={15} style={{ color: "#8A8A8A" }} />)}
                <MessageSquare size={14} style={{ color: "#C9A84C" }} />
                <span className="font-sans flex-1 truncate" style={{ fontSize: 14, color: "#E8E8E8" }}>{g.titulo}</span>
                <span className="font-sans" style={{ fontSize: 11, color: "#7A7A7A", whiteSpace: "nowrap" }}>{buscando ? `${g.matches.length}/${g.count}` : g.count} msj · {horaCorta(g.ultimo)}</span>
              </button>
              {open && (
                <div className="px-3 pb-3 space-y-2">
                  {g.matches.map((m) => (
                    <div key={m.id} className="rounded-lg p-3" style={{ background: m.rol === "asistente" ? "#0F0F0F" : "#141414", border: "1px solid #1F1F1F" }} data-testid={`chat-msg-${m.id}`}>
                      <div className="font-sans mb-1" style={{ fontSize: 11, color: "#6B6B6B" }}>{m.rol === "asistente" ? "Asistente" : (m.quien || "Cliente")} · {horaCorta(m.creado_en)}</div>
                      <div className="font-sans" style={{ fontSize: 13, color: "#D8D8D8", whiteSpace: "pre-wrap" }}>{m.texto}</div>
                      {m.rol === "asistente" && (
                        <button onClick={() => onMarcarMala(m.id)} data-testid={`marcar-mala-${m.id}`}
                          className="font-sans mt-2" style={{ fontSize: 11, color: m.marcada_mala ? "#D03B3B" : "#7A7A7A" }}>
                          {m.marcada_mala ? "✓ marcada como mala" : "marcar esta respuesta como mala"}
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
